import { create } from "zustand";
import i18n from "i18next";
import { ApiError } from "@/lib/api/errors";
import { errorTitle, formatSupportId } from "@/lib/api/error-display";

export type ToastVariant = "default" | "success" | "destructive";

export interface Toast {
	id: number;
	title: string;
	description?: string;
	variant: ToastVariant;
}

interface ToastState {
	toasts: Toast[];
	push: (toast: Omit<Toast, "id">) => number;
	dismiss: (id: number) => void;
}

let nextId = 1;

export const useToastStore = create<ToastState>((set) => ({
	toasts: [],
	push: (toast) => {
		const id = nextId++;
		set((state) => ({ toasts: [...state.toasts, { ...toast, id }] }));
		return id;
	},
	dismiss: (id) =>
		set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
}));

/** Imperative toast helpers usable outside React (e.g. query/mutation caches). */
export const toast = {
	success: (title: string, description?: string) =>
		useToastStore.getState().push({ title, description, variant: "success" }),
	error: (title: string, description?: string) =>
		useToastStore
			.getState()
			.push({ title, description, variant: "destructive" }),
	info: (title: string, description?: string) =>
		useToastStore.getState().push({ title, description, variant: "default" }),
};

/**
 * Surface an unknown error (ApiError or otherwise) as a destructive toast.
 *
 * Uses canonical error display helpers so every error code maps to the same
 * user-facing copy regardless of which surface triggers the toast. The
 * support ID (request_id) is shown when available.
 */
export function toastError(error: unknown, fallbackTitle?: string) {
	const t = i18n.t.bind(i18n);

	if (error instanceof ApiError) {
		const title = errorTitle(error, t);
		const supportId = formatSupportId(error, t);
		toast.error(title, supportId);
		return;
	}

	if (fallbackTitle) {
		toast.error(fallbackTitle);
		return;
	}

	toast.error(
		t("component.error.requestFailed"),
		error instanceof Error ? error.message : undefined,
	);
}
